// Country dialing codes for the phone-entry UI. Phones are stored as full
// E.164 (`+<code><national>`); this list backs the country-code selector so a
// number can never be entered without a code. Curated (not exhaustive) — common
// markets first, expandable. `iso` is the ISO 3166-1 alpha-2 country code.
//
// Order matters for the UI dropdown AND for parsing a stored +CC… value back
// into (code, national): match by LONGEST code prefix first (e.g. +1 vs +1242),
// so callers should sort by descending code length when parsing.

export interface DialCode {
  /** E.164 calling code with leading +, e.g. "+91". */
  code: string
  /** Human label for the selector, e.g. "India (+91)". */
  label: string
  /** ISO 3166-1 alpha-2, e.g. "IN". */
  iso: string
}

export const DIAL_CODES: readonly DialCode[] = [
  { code: '+91', label: 'India (+91)', iso: 'IN' },
  { code: '+1', label: 'USA / Canada (+1)', iso: 'US' },
  { code: '+44', label: 'United Kingdom (+44)', iso: 'GB' },
  { code: '+971', label: 'UAE (+971)', iso: 'AE' },
  { code: '+966', label: 'Saudi Arabia (+966)', iso: 'SA' },
  { code: '+974', label: 'Qatar (+974)', iso: 'QA' },
  { code: '+965', label: 'Kuwait (+965)', iso: 'KW' },
  { code: '+968', label: 'Oman (+968)', iso: 'OM' },
  { code: '+973', label: 'Bahrain (+973)', iso: 'BH' },
  { code: '+65', label: 'Singapore (+65)', iso: 'SG' },
  { code: '+60', label: 'Malaysia (+60)', iso: 'MY' },
  { code: '+61', label: 'Australia (+61)', iso: 'AU' },
  { code: '+64', label: 'New Zealand (+64)', iso: 'NZ' },
  { code: '+977', label: 'Nepal (+977)', iso: 'NP' },
  { code: '+880', label: 'Bangladesh (+880)', iso: 'BD' },
  { code: '+94', label: 'Sri Lanka (+94)', iso: 'LK' },
  { code: '+975', label: 'Bhutan (+975)', iso: 'BT' },
  { code: '+960', label: 'Maldives (+960)', iso: 'MV' },
  { code: '+92', label: 'Pakistan (+92)', iso: 'PK' },
  { code: '+93', label: 'Afghanistan (+93)', iso: 'AF' },
  { code: '+49', label: 'Germany (+49)', iso: 'DE' },
  { code: '+33', label: 'France (+33)', iso: 'FR' },
  { code: '+39', label: 'Italy (+39)', iso: 'IT' },
  { code: '+34', label: 'Spain (+34)', iso: 'ES' },
  { code: '+31', label: 'Netherlands (+31)', iso: 'NL' },
  { code: '+41', label: 'Switzerland (+41)', iso: 'CH' },
  { code: '+353', label: 'Ireland (+353)', iso: 'IE' },
  { code: '+27', label: 'South Africa (+27)', iso: 'ZA' },
  { code: '+254', label: 'Kenya (+254)', iso: 'KE' },
  { code: '+234', label: 'Nigeria (+234)', iso: 'NG' },
  { code: '+233', label: 'Ghana (+233)', iso: 'GH' },
  { code: '+255', label: 'Tanzania (+255)', iso: 'TZ' },
  { code: '+256', label: 'Uganda (+256)', iso: 'UG' },
  { code: '+81', label: 'Japan (+81)', iso: 'JP' },
  { code: '+82', label: 'South Korea (+82)', iso: 'KR' },
  { code: '+86', label: 'China (+86)', iso: 'CN' },
  { code: '+852', label: 'Hong Kong (+852)', iso: 'HK' },
  { code: '+63', label: 'Philippines (+63)', iso: 'PH' },
  { code: '+62', label: 'Indonesia (+62)', iso: 'ID' },
  { code: '+66', label: 'Thailand (+66)', iso: 'TH' },
]

/**
 * Split a stored E.164 value (`+<code><national>`) into its dial code and the
 * national part, by longest-prefix match against DIAL_CODES. Returns null if no
 * known code matches (e.g. a legacy bare number with no `+`).
 */
export function parseE164(value: string): { code: string; national: string } | null {
  if (!value || !value.startsWith('+')) return null
  const byLongest = [...DIAL_CODES].sort((a, b) => b.code.length - a.code.length)
  for (const dc of byLongest) {
    if (value.startsWith(dc.code)) {
      return { code: dc.code, national: value.slice(dc.code.length) }
    }
  }
  return null
}
